/**
 * @packageDocumentation
 * @module Shop-Packages-Component
 */

import { IReservation } from '../../interfaces/reservation.interfaces';
import { IRequestOptions, TOKEN_TYPES_ENUM } from 'tc-comp-services';
import { IVoucherPayload } from '../../../voucher/interfaces/voucher.interfaces';

/**
 * @ignore
 */
declare const TCCommonService;
/**
 * @ignore
 */
const $http = TCCommonService.$http;

/**
 * Api Service Class
 */
class ApiServiceClass {

    /**
     * Method to get hotel content
     * @param hotelCode
     * @param languageCode
     * @memberof ApiServiceClass
     */
    callHotelContentApi(hotelCode: number, languageCode?: string): any {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        let url = environmentConfig.basePath.API + 'be5-entity/v2/hotels/' + hotelCode + '/content?include=paymentinfo,roomtypes,packages,rateplans,settings,policyinfo,contactinfo,generalinformation,consents,alternatehotels';
        if (languageCode) {
            url = url + '&lang=' + languageCode;
        }
        return $http.get(url);
    }

    /**
    * Method to post GTM tracker metadata
    * @param storeMetaData
    * @memberof ApiServiceClass
    */
    callStoreMetaDataApi(storeMetaData): any {
        let url;
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const config = TCCommonService.ConfigService.getConfig();
        if (config.env === 'prod') {
            url = environmentConfig.basePath.API + 'meta-collection/v1/storeMetaData';
        } else {
            url = 'https://api-t6.travelclick.com/meta-collection/v1/storeMetaData';
        }
        return $http.post(url, JSON.stringify(storeMetaData));
    }

    /**
     * Method to get rooms, rate and package data
     * @param hotelCode
     * @param searchCriteria
     * @memberof ApiServiceClass
     */
    callPalsApi(hotelCode: number, searchCriteria) {
        let xTcHeaders;
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-shop/v2/hotels/' + hotelCode + '/avail';
        if (searchCriteria?.includeMemberRate && searchCriteria.tpaExtension.length) {
            xTcHeaders = 'loyaltyTierId=' + searchCriteria.tpaExtension[0].value;
            delete searchCriteria['includeMemberRate'];
            headers.append('x-tc-header', xTcHeaders);
            return $http.post(url, JSON.stringify(searchCriteria), headers);
        } else {
            return $http.post(url, JSON.stringify(searchCriteria));
        }
    }

    /**
     * Method to hold a reservation
     * @param hotelCode
     * @param payload
     */
    callHoldReservation(hotelCode: number, payload, loyaltyTierId: number = null, currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/hold-reservation/multi-room';
        let xTcHeaders;
        if (loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + loyaltyTierId;
        }
        if (currencyCode) {
            xTcHeaders ? (xTcHeaders += ',currency=' + currencyCode)
                : (xTcHeaders = 'currency=' + currencyCode);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.post(url, JSON.stringify(payload), headers);
    }

    /**
     * Method to enhance upsell reservation
     * @param hotelCode
     * @param payload
     */
    upSellReservation(hotelCode: number, requestPayload: IReservation, loyaltyTierId: number = null,
        currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/reservation/' + requestPayload.uniqueId + '/upsell';
        let xTcHeaders;
        if (loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + loyaltyTierId;
        }
        if (currencyCode) {
            xTcHeaders ? (xTcHeaders += ',currency=' + currencyCode)
                : (xTcHeaders = 'currency=' + currencyCode);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.put(url, JSON.stringify(requestPayload), headers);
    }

    /**
     * Method to commit a reservation
     * @param hotelCode
     * @param payload
     */
    callCommitReservation(hotelCode: number, payload, loyaltyTierId: number = null, currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/reservation/multi-room';
        let xTcHeaders;
        if (loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + loyaltyTierId;
        }
        if (currencyCode) {
            xTcHeaders ? (xTcHeaders += ',currency=' + currencyCode)
                : (xTcHeaders = 'currency=' + currencyCode);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.post(url, JSON.stringify(payload), headers);
    }

    /**
     * Method to call basic avail api
     * @param hotelCode
     * @param payload
     * @memberof ApiServiceClass
     */
    callBasicAvailApi(hotelCode: number, payload, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-shop/v1/hotel/' + hotelCode + '/basicavail/multi-room';
        headers.append('tcRequestOptions', JSON.stringify({ monitorRequest: false }));
        let xTcHeaders;
        if (payload.loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + payload.loyaltyTierId;
        }
        if (payload.currency) {
            xTcHeaders ? (xTcHeaders += ',currency=' + payload.currency)
                : (xTcHeaders = 'currency=' + payload.currency);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }

        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.post(url, JSON.stringify(payload), headers);
    }

    /**
     * Method to get countries
     * @memberof ApiServiceClass
     */
    getCountries() {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-globals-data/v1/countries?include=states';
        return $http.get(url);
    }

    /**
     * Method to get product services
     * @param hotelCode
     * @param queryParams
     * @param disableMonitorRequest
     * @param enableApiCaching
     * @memberof ApiServiceClass
     */
    getProductServices(hotelCode: number, queryParams: string, disableMonitorRequest: boolean = false,
        currencyCode?: string, tcShareLocation?: boolean, loyaltyTierId?: number) {
        const headers = new Headers();
        headers.append('tcRequestOptions', JSON.stringify({ monitorRequest: !disableMonitorRequest }));
        let xTcHeaders;
        if (currencyCode) {
            xTcHeaders = 'currency=' + currencyCode;
        }
        if (loyaltyTierId) {
            xTcHeaders ? (xTcHeaders += ',loyaltyTierId=' + loyaltyTierId)
                : (xTcHeaders = 'loyaltyTierId=' + loyaltyTierId);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-shop/v1/hotel/' + hotelCode + '/services?' + queryParams;
        return $http.get(url, headers);
    }

    /**
     * Method to validate special code info
     * @param hotelCode
     * @param codeType
     * @param codeValue
     * @memberof ApiServiceClass
     */
    validateSpecialCode(hotelCode: number, codeType: string, codeValue: string, languageId: string,
        currencyCode?: string, tcShareLocation?: boolean, loyaltyTierId?: number) {
        const headers = new Headers();
        let xTcHeaders;
        let userLoggedIn;
        if (currencyCode) {
            xTcHeaders = 'currency=' + currencyCode;
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (loyaltyTierId) {
            xTcHeaders ? (xTcHeaders += ',loyaltyTierId=' + loyaltyTierId)
                : (xTcHeaders = 'loyaltyTierId=' + loyaltyTierId);
            userLoggedIn = true;
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        let url = environmentConfig.basePath.API + 'be5-codes/v1/hotel/' + hotelCode + '/specialcodes/' + codeType + '/' + codeValue;
        url = languageId ? url + '?lang=' + languageId : url;
        if (userLoggedIn) {
            if (languageId) {
                url = url + '&userLoggedIn=' + userLoggedIn;
            } else {
                url = url + '?userLoggedIn=' + userLoggedIn;
            }
        }
        return $http.get(url, headers);
    }

    /**
     * Method validate groupId
     * @param hotelCode
     * @param groupId
     * @param languageId
     * @memberof ApiServiceClass
     */
    validateGroupId(hotelCode: number, groupId: number, languageId: string,
        currencyCode?: string, tcShareLocation?: boolean, loyaltyTierId?: number) {
        const headers = new Headers();
        let xTcHeaders;
        let userLoggedIn;
        if (currencyCode) {
            xTcHeaders = 'currency=' + currencyCode;
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (loyaltyTierId) {
            xTcHeaders ? (xTcHeaders += ',loyaltyTierId=' + loyaltyTierId)
                : (xTcHeaders = 'loyaltyTierId=' + loyaltyTierId);
            userLoggedIn = true;
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        let url = environmentConfig.basePath.API + 'be5-codes/v1/hotel/' + hotelCode + '/specialcodes/group/groupid/' + groupId;
        url = languageId ? url + '?lang=' + languageId : url;
        if (userLoggedIn) {
            if (languageId) {
                url = url + '&userLoggedIn=' + userLoggedIn;
            } else {
                url = url + '?userLoggedIn=' + userLoggedIn;
            }
        }
        return $http.get(url, headers);
    }

    /**
     * Method to retrieve reservation
     * @param reservationCode
     * @param lastName
     * @param languageId
     */
    retrieveReservation(hotelCode: number, reservationCode: number, lastName: string, languageId: string,
        currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        let xTcHeaders;

        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders = 'currency=' + currencyCode;
            xTcHeaders += ',tcShareLocation=' + tcShareLocation;
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        let url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/reservation/' + reservationCode +
            '?surName=' + lastName;
        url = languageId ? url + '&lang=' + languageId : url;
        // url = url + '&bookingSource=CRS';
        return $http.get(url, headers);
    }

    /**
     * Method to call modify hold reservation api
     * @param hotelCode
     * @param requestPayload
     */
    callModifyHoldReservation(hotelCode: number, requestPayload: IReservation, loyaltyTierId: number = null,
        currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/hold-reservation/' + requestPayload.uniqueId;
        let xTcHeaders;
        if (loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + loyaltyTierId;
        }
        if (currencyCode) {
            xTcHeaders ? (xTcHeaders += ',currency=' + currencyCode)
                : (xTcHeaders = 'currency=' + currencyCode);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.put(url, JSON.stringify(requestPayload), headers);
    }

    /**
     * Method to call modify commit api
     * @param hotelCode
     * @param requestPayload
     */
    callModifyCommitReservation(hotelCode: number, requestPayload: IReservation, loyaltyTierId: number = null,
        currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/reservation/' + requestPayload.uniqueId;
        let xTcHeaders;
        if (loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + loyaltyTierId;
        }
        if (currencyCode) {
            xTcHeaders ? (xTcHeaders += ',currency=' + currencyCode)
                : (xTcHeaders = 'currency=' + currencyCode);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.put(url, JSON.stringify(requestPayload), headers);
    }

    /**
     * Method to cancel reservation
     * @param hotelCode
     * @param reservationCode
     * @param lastName
     */
    cancelReservation(hotelCode: number, reservationCode: number, lastName: string) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/reservation/' + reservationCode +
            '?surName=' + lastName;
        return $http.delete(url);
    }

    /**
     * Method to cancel reservation
     * @param hotelCode
     * @param reservationCode
     * @param email
     */
    cancelExtendedHoldReservation(hotelCode: number, reservationCode: number, email: string) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/reservation/' + reservationCode +
            '?email=' + email;
        return $http.delete(url);
    }

    /**
     * Method to get hotel content
     * @param hotelCode
     * @param languageCode
     * @memberof ApiServiceClass
     */
    getBraintreeConfigurations(hotelCode: number): any {
        const headers = new Headers();
        headers.append('hotelId', hotelCode.toString());
        headers.append('tcRequestOptions', JSON.stringify({ monitorRequest: true }));
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'braintree/v1/payments/metadata';
        return $http.get(url, headers);
    }

    /**
     * Method to get exchange rates of api
     * @param currencyCode
     * @returns
     */
    getCurrencyExchangeRates(currencyCode: string) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-globals-data/v1/currency/' + currencyCode + '/exchange-rates';
        return $http.get(url);
    }

    /**
     * Method to return loyalty guest profile
     * @param chainCode
     * @param memberToken
     * @param memberId
     * @returns
     */
    getLoyaltyGuestProfile(chainCode: string, memberToken: string, memberId: string) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'loyalty/v2/' + chainCode + '/account/' + memberId;
        const headers = new Headers();
        const tcRequestOptions: IRequestOptions = { tokenType: TOKEN_TYPES_ENUM.Gms };
        headers.append('x-tc-user-auth', memberToken);
        headers.append('tcRequestOptions', JSON.stringify(tcRequestOptions));
        return $http.get(url, headers);
    }

    /**
    * Method to Extended hold a reservation
    * @param hotelCode
    * @param payload
    * @returns
    */
    callExtendedHoldReservation(hotelCode: number, payload, loyaltyTierId: number = null,
        currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/hold-reservation/exd-hold';
        let xTcHeaders;
        if (loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + loyaltyTierId;
        }
        if (currencyCode) {
            xTcHeaders ? (xTcHeaders += ',currency=' + currencyCode)
                : (xTcHeaders = 'currency=' + currencyCode);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.post(url, JSON.stringify(payload), headers);
    }
    /**
    * Method to get extended reservation
    * @param hotelCode
    * @param reservationCode
    * @param emailId
    * @param languageId
    * @returns
    */
    getExtendedReservation(hotelCode: number, reservationCode: number, emailId: string, languageId: string, loyaltyTierId: number = null,
        currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        let url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/reservation/' + reservationCode +
            '/exd-reservation' + '?email=' + emailId;
        url = languageId ? url + '&lang=' + languageId : url;
        let xTcHeaders;
        if (loyaltyTierId) {
            xTcHeaders = 'loyaltyTierId=' + loyaltyTierId;
        }
        if (currencyCode) {
            xTcHeaders ? (xTcHeaders += ',currency=' + currencyCode)
                : (xTcHeaders = 'currency=' + currencyCode);
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        return $http.get(url, headers);
    }

    /**
     * Method to get voucher details
     */
    getVoucherDetails(partner: string, voucherPayload: IVoucherPayload) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'vouchers/v1/' + partner.toLowerCase() + '/status';
        return $http.post(url, JSON.stringify(voucherPayload));
    }

    /**
     * Method to save abandonment
     */
    saveAbandonment(payload) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.abandonment + 'abandonment/tc_web_nav.php';
        const headers = new Headers();
        const tcRequestOptions: IRequestOptions = {
            tokenType: null,
            excludeAuthorizationToken: true,
            appendContentType: false
        };
        headers.append('tcRequestOptions', JSON.stringify(tcRequestOptions));
        return $http.post(url, JSON.stringify(payload), headers);
    }

    /**
     * Method to initiate payment init
     * @param hotelCode
     * @param payload
     * @returns
     */
    initiatePayment(hotelCode, payload, currencyCode?: string, tcShareLocation?: boolean) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-book/v1/hotel/' + hotelCode + '/payment-init';
        const headers = new Headers();
        let xTcHeaders;
        if (currencyCode) {
            xTcHeaders = 'currency=' + currencyCode;
        }
        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders ? (xTcHeaders += ',tcShareLocation=' + tcShareLocation)
                : (xTcHeaders = 'tcShareLocation=' + tcShareLocation);
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        headers.append('tcRequestOptions', JSON.stringify({ monitorRequest: false }));
        return $http.post(url, JSON.stringify(payload), headers);
    }

    /**
     * @param hotelCode get media of associated with this hotel code
     * @param entityIncludeStr this string contains rooms,services,proprty
     * @param entityType is string contains rooms,packages,property etc. to get media for rooms,packages, property
     * @param entityId if we are getting images for group then passing groupId
     * @param languageCode language if needed
     * @returns
     */

    getHotelMediaInfo(hotelCode: number, entityIncludeStr?: string, entityType?: string, entityId?: number, languageCode?: string): any {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        let url = environmentConfig.basePath.API + 'be5-entity/media/v1/hotel/' + hotelCode + '/media';
        if (entityIncludeStr) {
            url = url + '?includes=' + entityIncludeStr;
            if (languageCode) {
                url = url + '&lang=' + languageCode;
            }
        }
        if (entityType) {
            url = url + '/' + entityType;
            if (entityId) {
                url = url + '/' + entityId;
            }
            if (languageCode) {
                url = url + '?lang=' + languageCode;
            }
        }

        return $http.get(url);
    }

    getAmadeusPaymentMetaData(hotelCode: number, payload): any {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const headers = new Headers();
        headers.append('hotelId', JSON.stringify(hotelCode));
        headers.append('tcRequestOptions', JSON.stringify({ monitorRequest: false }));
        const url = environmentConfig.basePath.API + 'payments/amadeus/v1/metadata';
        return $http.post(url, JSON.stringify(payload), headers);
    }

    getAmadeusToken(featureName: string, hotelCode: string): any {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const headers = new Headers();
        headers.append('hotelId', hotelCode);
        headers.append('featureName', featureName);
        const url = environmentConfig.basePath.API + 'payments/amadeus/v1/token';
        return $http.post(url, null, headers);
    }

    getAdvertisementsDetails(hotelCode: number, languageCode?: string, chainCode?: string, currencyCode?: string) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        let url = environmentConfig.basePath.API + 'be5-codes/v1/hotel/' + hotelCode + '/merchandise' + '?channelType=ibe_4';
        url = languageCode ? url + '&lang=' + languageCode : url;
        url = currencyCode ? url + '&currency=' + currencyCode : url;
        url = chainCode ? url + '&chainCode=' + chainCode : url;
        const headers = new Headers();
        headers.append('tcRequestOptions', JSON.stringify({ monitorRequest: false }));
        return $http.get(url, headers);
    }

    getAdvertisementById(hotelCode: number, advertisementId: number) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-codes/v1/hotel/' + hotelCode + '/merchandise/' + advertisementId;
        const headers = new Headers();
        headers.append('tcRequestOptions', JSON.stringify({ monitorRequest: false }));
        return $http.get(url, headers);
    }

    /**
     * Method to get alternate properties data
     * @param hotelCode
     * @param searchCriteria
     * @memberof ApiServiceClass
     */
    getAlternateProperties(hotelCode: number, searchCriteria) {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = environmentConfig.basePath.API + 'be5-shop/v2/hotels/' + hotelCode + '/alternatehotelavail';
        return $http.post(url, JSON.stringify(searchCriteria));
    }

    /**
     * Method to call cybersource 3ds v2 payer setup
     * @param hotelCode
     * @param payload
     * @memberof ApiServiceClass
     */
    getCybersourcePayerAuthSetUp(hotelCode: number, payload: any): any {
        const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const headers = new Headers();
        const tcRequestOptions: IRequestOptions = {
            tokenType: null,
            excludeAuthorizationToken: false
        };
        headers.append('tcRequestOptions', JSON.stringify(tcRequestOptions));
        headers.append('hotelId', JSON.stringify(hotelCode));
        const url = environmentConfig.basePath.API + 'cybersource/v2/payments/ics_pa_setup';
        return $http.post(url, JSON.stringify(payload), headers);
    }
    /**
     * Method to retrieve reservation
     * @param hotelCode
     * @param confirmationId confirmationId will be in encrypted string
     * @param checkInDate check in date format 'yyy-mm-dd'
     * @param currencyCode
     * @param tcShareLocation
     * @returns reservation
     */
    retrievePayByLinkReservation(hotelCode: number, confirmationId: string,
        checkInDate: string, currencyCode?: string, tcShareLocation?: boolean) {
        const headers = new Headers();
        let xTcHeaders;

        if (tcShareLocation || tcShareLocation === false) {
            xTcHeaders = 'currency=' + currencyCode;
            xTcHeaders += ',tcShareLocation=' + tcShareLocation;
        }
        if (xTcHeaders) {
            headers.append('x-tc-header', xTcHeaders);
        }
        // const environmentConfig = TCCommonService.ConfigService.getEnvironmentConfig();
        const url = 'http://api-internal-t4.travelclick.com/' + 'ibe-book/v1/hotel/' + hotelCode + '/reservation/' + confirmationId +
            '?isPayByLink=true&checkInDate=' + checkInDate;
        return $http.get(url, headers);
    }
}

export const ApiService = new ApiServiceClass();
